import fetch from '@adc/fetch'
import { gql } from '@adc/sdk'
import type {
  AuctionTransaction,
  CollectionResult,
  Maybe,
} from '@adc/sdk/types'

const isTermInQuotes = (term: string) =>
  (term.startsWith('"') && term.endsWith('"')) ||
  (term.startsWith("'") && term.endsWith("'"))

const isNumericId = (term: string) => /^\d+$/.test(term.trim())

export const TransactionSearchFieldFragment = `
fragment TransactionSearchFields on AuctionTransaction {
  transaction_id
  transaction_urn
  created_date
  associated_urns {
    associated_urn
  }
  listing {
    listing_id
    is_searchable
    is_published
    formatted_address(format: DOUBLE_LINE)
    primary_photo
    listing_status
    listing_status_label
    listing_configuration {
      product_type
    }
  }
  property {
    property_id
    summary {
      address {
        street_number
        street_name
        municipality
        country_primary_subdivision
        postal_code
      }
      total_bedrooms
      total_bathrooms
      square_footage
      structure_type_code
      year_built
    }
  }
  parties {
    party_name
    party_role
    party_responsibility
  }
}`

export type TransactionSearchResult = AuctionTransaction & {
  transaction_id: string
  transaction_urn: string
}

export const searchTransactions = async (
  searchTerm: string
): Promise<Maybe<TransactionSearchResult>[]> => {
  let dsl: Record<string, unknown> = {}

  if (isTermInQuotes(searchTerm)) {
    // Exact match on transaction_id, listing_id, or property_id
    const stripped = searchTerm.slice(1, -1)
    dsl = {
      or: {
        expressions: [
          {
            equals: {
              fields: 'transaction_id.keyword',
              values: [stripped],
            },
          },
          {
            equals: {
              fields: 'listing.listing_id',
              values: [stripped],
            },
          },
          {
            equals: {
              fields: 'property.property_id',
              values: [stripped],
            },
          },
        ],
      },
    }
  } else if (isNumericId(searchTerm)) {
    // Numeric input — could be transaction_id, listing_id, property_id, or street number
    dsl = {
      or: {
        expressions: [
          {
            equals: {
              fields: 'transaction_id.keyword',
              values: [searchTerm.trim()],
            },
          },
          {
            equals: {
              fields: 'listing.listing_id',
              values: [searchTerm.trim()],
            },
          },
          {
            equals: {
              fields: 'property.property_id',
              values: [searchTerm.trim()],
            },
          },
          {
            query_string: {
              fields: [
                'property.summary.address.street_number',
                'property.summary.address.postal_code',
              ],
              value: `*${searchTerm.trim()}*`,
            },
          },
        ],
      },
    }
  } else {
    // Free text — search address fields and transaction_urn
    // Build query_string value as "*term1* AND *term2*" to match across split fields
    const terms = searchTerm.trim().split(/\s+/)
    const queryValue = terms.map(t => `*${t}*`).join(' AND ')

    dsl = {
      or: {
        expressions: [
          {
            query_string: {
              fields: ['transaction_urn'],
              value: `*${searchTerm.trim()}*`,
            },
          },
          {
            query_string: {
              fields: [
                'property.summary.address.street_number',
                'property.summary.address.street_name',
                'property.summary.address.municipality',
                'property.summary.address.country_primary_subdivision',
                'property.summary.address.postal_code',
              ],
              value: queryValue,
            },
          },
        ],
      },
    }
  }

  const response = await fetch.forAuctionGraph<'seek', CollectionResult>({
    query: gql`
      query toolkitCore_searchTransactions($dsl: String!) {
        seek(dsl: $dsl, type: AUCTION_TRANSACTIONS) {
          total_count
          content {
            ...TransactionSearchFields
          }
        }
      }
      ${TransactionSearchFieldFragment}
    `,
    variables: {
      dsl: JSON.stringify({ query: dsl }),
    },
  })

  const content = response?.body?.data?.seek?.content || []
  return content as Maybe<TransactionSearchResult>[]
}
